import { useEffect, useState } from 'react';
import { IconEditOrders, IconWarning } from '../../../../../../assets/DesignSystem/SVGComponents';
import { CopyIcon } from 'assets/images/shopping';
import {
  IReportBody,
  IReportingOptions,
  IlogisticServiceDetails,
  LOCATION_PLACE_TYPE,
  LOGISTIC_SERVICE_STATUS_NAME
} from '../../../../../../types/services';
import LogisticServiceNudosLocation from '../LogisticServiceNudosLocation/LogisticServiceNudosLocation';
import ConfirmAddressChangeModal from '../ConfirmAddressChangeModal/ConfirmAddressChangeModal';
import { checkAddressChanged, optionsReportAddressIssue } from '../../../../../../services/services';
import { InformationIcon } from '../../../../../../assets/images/shopping';
import { BlackCheck } from '../../../../../countriesList/components';
import LogisticServiceEmployeeOfficeLocation from '../LogisticServiceEmployeeOfficeLocation/LogisticServiceEmployeeOfficeLocation';
import { LogisticsServiceReportingOptions } from '../LogisticsServiceReportingOptions';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../utils/displayNudosStandardNotifications';
import useServiceState from '../../../../../../state/useServicesState';
import { LoadingOutlined } from '@ant-design/icons';

const LogisticServiceOriginDestinationLocation = ({
  logisticServiceData,
  updateDataCallback,
  sameCity,
  updateWithoutLoader,
  reportingOptionsList,
  location
}: {
  logisticServiceData: IlogisticServiceDetails;
  updateDataCallback: () => Promise<void>;
  sameCity: boolean;
  updateWithoutLoader: () => Promise<void>;
  reportingOptionsList?: IReportingOptions[];
  location: 'origin' | 'destination';
}) => {
  const {
    destinationDataChangedCheck,
    setDestinationDataChangedCheck,
    originDataChangedCheck,
    setOriginDataChangedCheck
  } = useServiceState();

  const [openConfirmModal, setOpenConfirmeModal] = useState<boolean>();
  const [reportAddressLoading, setReportAddressLoading] = useState<boolean>();
  const [checkChangedLoading, setCheckChangedLoading] = useState<boolean>();
  const [destinationReportingOptionsList, setDestinationReportingOptionsList] = useState<IReportingOptions[]>([]);

  const [openReportList, setOpenReportList] = useState<boolean>(false);
  const [selectedReportingOptions, setSelectedReportingOptions] = useState<IReportingOptions[]>([]);
  const [enableEditAddress, setEnableEditAddress] = useState<boolean>(false);
  const [loadingResetData, setLoadingResetData] = useState<boolean>(false);

  const { statusName, destinationLocation, originLocation, addressIssue } = logisticServiceData;

  const locationIsOrigin = location === 'origin';
  const locationText = locationIsOrigin ? 'origen' : 'destino';
  const originDestinationData = locationIsOrigin ? originLocation : destinationLocation;
  const originDestinationIssue = locationIsOrigin ? addressIssue?.origin : addressIssue?.destination;
  const employeeDestination = originDestinationData?.place === LOCATION_PLACE_TYPE?.USER;
  const viewedChanges = !!originDestinationIssue?.viewedChanges;
  const destinationAddressEdited = originDestinationIssue?.editedBy === LOCATION_PLACE_TYPE?.USER;
  const originDestinationChangedCheck = locationIsOrigin ? originDataChangedCheck : destinationDataChangedCheck;
  const blackCheckedValue = !!viewedChanges || !!originDestinationChangedCheck;

  const isNudosLocation = [
    LOCATION_PLACE_TYPE?.NUDOS,
    LOCATION_PLACE_TYPE?.DIAGNOSTIC,
    LOCATION_PLACE_TYPE?.WARRANTY
  ].includes((originDestinationData?.place || '') as LOCATION_PLACE_TYPE);

  const isOfficeLocation = [LOCATION_PLACE_TYPE?.OFFICE, LOCATION_PLACE_TYPE?.OTHER].includes(
    (originDestinationData?.place || '') as LOCATION_PLACE_TYPE
  );

  const canceledStatusName = statusName === LOGISTIC_SERVICE_STATUS_NAME?.CANCELLED;
  const deliveredStatusName = statusName === LOGISTIC_SERVICE_STATUS_NAME?.DELIVERED;
  const displayEmployeeOfficeLocation = employeeDestination || isOfficeLocation;

  const displayReportAndEdit = !isNudosLocation && !canceledStatusName && !deliveredStatusName && !enableEditAddress;

  const copyAddress = () => {
    const fotmatCopyText = `Datos de ${locationText} :avión_aterrizando: \nPaís: ${
      originDestinationData?.country?.name || 'Sin país'
    }\nCiudad: ${originDestinationData?.city || 'Sin ciudad'}\nDirección: ${
      originDestinationData?.address || 'Sin dirección'
    }\nIndicaciones: ${originDestinationData?.additionalReferences || 'Sin indicaciones'}\nCódigo Postal: ${
      originDestinationData?.zipCode || 'Sin código postal'
    }`;
    navigator.clipboard.writeText(fotmatCopyText);
  };

  const reportOriginAddressIssue = async () => {
    if (!logisticServiceData?.id) return;
    setReportAddressLoading(true);
    try {
      let ids: number[] = selectedReportingOptions.map(reporting => reporting.id);
      const reportBody: IReportBody = {
        logisticServiceId: Number(logisticServiceData?.id),
        location: location,
        wrongFields: ids
      };
      await optionsReportAddressIssue(reportBody);
      setOpenConfirmeModal(false);
      displaySuccessNotification({
        customJSXMessage: <>Reporte del problema con la información enviado con éxito.</>
      });
      await updateDataCallback();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setReportAddressLoading(false);
    }
  };

  const checkDestinationAddressChanged = async () => {
    if (!logisticServiceData?.id || !!blackCheckedValue) return;
    try {
      setCheckChangedLoading(true);
      await checkAddressChanged(logisticServiceData?.id, location);
      locationIsOrigin ? setOriginDataChangedCheck(true) : setDestinationDataChangedCheck(true);
      await updateDataCallback();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setCheckChangedLoading(false);
    }
  };

  const handleSelectOptions = (option: IReportingOptions) => {
    const findItem = selectedReportingOptions.find(reportOption => reportOption.id === option.id);
    if (findItem) {
      const filterList = selectedReportingOptions.filter(reportOption => reportOption.id !== option.id);
      setSelectedReportingOptions([...filterList]);
    } else {
      setSelectedReportingOptions([...selectedReportingOptions, option]);
    }
  };

  const continueReport = () => {
    setOpenConfirmeModal(true);
    setOpenReportList(false);
  };

  const initialOptions = () => {
    if (reportingOptionsList && reportingOptionsList.length > 0)
      setDestinationReportingOptionsList([...reportingOptionsList]);
  };

  const updateTab2 = async () => {
    setLoadingResetData(true);
    await updateWithoutLoader();
    displaySuccessNotification({
      customJSXMessage: <>Se restablecieron los datos.</>
    });
    setEnableEditAddress(false);
    setLoadingResetData(false);
  };

  useEffect(() => {
    initialOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportingOptionsList]);

  return (
    <div className="LogisticServiceRequesterInformation">
      {openConfirmModal && selectedReportingOptions && selectedReportingOptions.length > 0 && (
        <ConfirmAddressChangeModal
          logisticServiceData={logisticServiceData}
          submitModalCallback={() => reportOriginAddressIssue()}
          closeModalCallback={() => setOpenConfirmeModal(false)}
          location={location}
          loading={reportAddressLoading}
          selectedReportingOptions={selectedReportingOptions}
        />
      )}
      <div className="logisticServiceDetailsTab2SectionTitle">
        Datos de {locationText}
        <div className="copyAddressText">
          <div className="boxContainerCopyAddressText" onClick={() => copyAddress()}>
            <div className="blueCopyText">Copiar información</div>
            <div className="copyIconBox">
              <CopyIcon />
            </div>
          </div>
        </div>
        {!!destinationAddressEdited && !checkChangedLoading && (
          <div className={`updateLocationContainer ${blackCheckedValue ? 'viewedChanges' : ''}`}>
            <InformationIcon className="informationIcon" />
            <div className="updateLocationText">{`Se actualizó la información de ${locationText}`}</div>
            <BlackCheck handleChangeCheck={() => checkDestinationAddressChanged()} checked={blackCheckedValue} />
            <div>Entendido</div>
          </div>
        )}
        {destinationAddressEdited && checkChangedLoading && (
          <div className={`containerSkeletonLoader animationLoader`} />
        )}
      </div>
      {isNudosLocation && (
        <LogisticServiceNudosLocation
          place={originDestinationData?.place}
          countryName={originDestinationData?.country?.name}
        />
      )}
      {displayEmployeeOfficeLocation && !loadingResetData && (
        <LogisticServiceEmployeeOfficeLocation
          location={location}
          enableEditAddress={enableEditAddress}
          updateDataCallback={updateDataCallback}
          logisticServiceData={logisticServiceData}
          updateWithoutLoader={updateTab2}
        />
      )}
      {displayEmployeeOfficeLocation && loadingResetData && (
        <div
          className="w-full flex items-center  content-center justify-center"
          style={{ height: '232px', width: '772px' }}
        >
          <LoadingOutlined style={{ color: '#070F21', fontSize: '50px' }} />
        </div>
      )}
      {displayReportAndEdit && (
        <div className="reportAndEditContainer">
          <div className="editContainer" onClick={() => setEnableEditAddress(true)}>
            Editar información <IconEditOrders />
          </div>
          <div className="reportIssueContainer">
            <div onClick={() => setOpenReportList(!openReportList)}>Reportar</div>
            <IconWarning />
            {openReportList && (
              <LogisticsServiceReportingOptions
                reportingOptionsList={destinationReportingOptionsList}
                selectedReportingOptions={selectedReportingOptions}
                sameCity={sameCity}
                handleSelectOptions={handleSelectOptions}
                loading={false}
                disabled={!selectedReportingOptions?.length}
                location={location}
                handleContinue={continueReport}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogisticServiceOriginDestinationLocation;
